import { useEffect, useState } from "react";
import { LifeBuoy, Send, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import axios, { API, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";

export const Support = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [requestType, setRequestType] = useState("general");
  const [isAgency, setIsAgency] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    axios.get(`${API}/audits/mine/list`).then((res) => {
      setIsAgency(res.data.some((a) => a.paid_tier === "agency"));
    }).catch(() => {});
    axios.get(`${API}/support/mine`).then((res) => setTickets(res.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/support/submit`, { subject, message, request_type: requestType });
      toast.success("Your request has been sent to the Sovereign Directorate.");
      setSubject("");
      setMessage("");
      const res = await axios.get(`${API}/support/mine`);
      setTickets(res.data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="support-page">
      <div className="mb-3 flex items-center gap-2">
        <LifeBuoy className="h-6 w-6 text-sovereign" />
        <h1 className="font-heading text-4xl font-bold text-white">Support &amp; Feedback</h1>
      </div>
      <p className="mb-8 text-slate-400">Submit a request directly to the Sovereign Directorate.</p>

      <div className="mb-8 flex gap-2" data-testid="support-type-toggle">
        <Button
          type="button"
          variant={requestType === "general" ? "default" : "outline"}
          onClick={() => setRequestType("general")}
          className={requestType === "general" ? "bg-sovereign text-white hover:bg-sovereign/90" : "border-surface-border text-slate-300"}
          data-testid="support-type-general-btn"
        >
          General Support
        </Button>
        <Button
          type="button"
          variant={requestType === "custom_intelligence" ? "default" : "outline"}
          onClick={() => setRequestType("custom_intelligence")}
          disabled={!isAgency}
          title={!isAgency ? "Requires an active Sovereign Agency ($799) plan" : undefined}
          className={
            requestType === "custom_intelligence"
              ? "gap-2 bg-sovereign text-white hover:bg-sovereign/90"
              : "gap-2 border-surface-border text-slate-300 disabled:cursor-not-allowed disabled:opacity-40"
          }
          data-testid="support-type-custom-intel-btn"
        >
          <ShieldCheck className="h-4 w-4" /> Custom Intelligence Request
        </Button>
      </div>

      <form onSubmit={submit} className="space-y-4 rounded-lg border border-surface-border bg-surface-card p-6" data-testid="support-form">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Subject</p>
          <Input
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="border-surface-border bg-obsidian text-white"
            data-testid="support-subject-input"
          />
        </div>
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Message</p>
          <Textarea
            required
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="border-surface-border bg-obsidian text-white"
            data-testid="support-message-input"
          />
        </div>
        <Button type="submit" disabled={submitting} className="gap-2 bg-sovereign text-white hover:bg-sovereign/90" data-testid="support-submit-btn">
          <Send className="h-4 w-4" /> {submitting ? "Sending..." : "Send Request"}
        </Button>
      </form>

      {tickets.length > 0 && (
        <div className="mt-10">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Your Past Requests</p>
          <div className="space-y-2" data-testid="support-tickets-list">
            {tickets.map((t) => (
              <div key={t.id} className="rounded-lg border border-surface-border bg-surface-card p-4" data-testid={`support-ticket-${t.id}`}>
                <p className="font-heading text-sm font-semibold text-white">{t.subject}</p>
                <p className="mt-1 text-sm text-slate-500">{t.message}</p>
                <p className="mt-2 text-xs text-slate-600">{t.request_type === "custom_intelligence" ? "Custom Intelligence Request" : "General Support"} - {t.status}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
